import { icons } from '@lifebit-ai/lds'
import { ResultsState } from 'enums'
import React from 'react'

import {
  DATA_ACCESS_REQUEST_DETAILS_UNAVAILABLE_MESSAGE,
  DATA_ACCESS_REQUEST_FORM_TITLE
} from 'modules/resultsV2/utils'
import { DataAccessRequestReviewSubStatus, DataAccessRequestStatus } from 'services/data-access-requests/enums'
import type { DataAccessRequestDetail, DataAccessRequestSharing } from 'services/data-access-requests/types'
import type { Result } from 'services/results/types'
import type { StudyId } from 'services/shared/schemas/studyIdSchema'

import { DataAccessRequestGenotypicQuerySection } from './DataAccessRequestGenotypicQuerySection'
import { DataAccessRequestMetadataSection } from './DataAccessRequestMetadataSection'
import { DataAccessRequestPhenotypicQuerySection } from './DataAccessRequestPhenotypicQuerySection'
import * as styles from './ResultMeta.styles'
import { getDataAccessRequestFormCardData } from './hooks/getDataAccessRequestFormCardData'

export {
  FEDERATED_WORKSPACE_INFO_TEXT,
  ORGANISATION_INFO_TEXT,
  REQUESTING_WORKSPACE_INFO_TEXT,
  TEST_ID_DATA_ACCESS_REQUEST_ADDITIONAL_INFORMATION_METADATA,
  TEST_ID_DATA_ACCESS_REQUEST_DATASET_COLOR,
  TEST_ID_DATA_ACCESS_REQUEST_METADATA,
  TEST_ID_DATA_ACCESS_REQUEST_PARTICIPANT_COUNT,
  TEST_ID_DATA_ACCESS_REQUEST_SHARING_STATUS,
  TEST_ID_DATA_ACCESS_REQUEST_STATUS,
  TEST_ID_DATA_ACCESS_REQUEST_STUDY_ID
} from './DataAccessRequestMetadataSection'
export { TEST_ID_DATA_ACCESS_REQUEST_GENOTYPIC_QUERY } from './DataAccessRequestGenotypicQuerySection'
export {
  TEST_ID_DATA_ACCESS_REQUEST_PHENOTYPIC_QUERY,
  TEST_ID_DATA_ACCESS_REQUEST_QUERY_PREVIEW
} from './DataAccessRequestPhenotypicQuerySection'

export const TEST_ID_DATA_ACCESS_REQUEST_UNAVAILABLE = 'data-access-request-unavailable'

const mapResultsStateToDataAccessRequestStatus = (status: ResultsState): DataAccessRequestStatus => {
  switch (status) {
    case ResultsState.Complete:
      return DataAccessRequestStatus.Approved
    case ResultsState.Rejected:
      return DataAccessRequestStatus.Rejected
    default:
      return DataAccessRequestStatus.PendingVerification
  }
}

interface DataAccessRequestFormCardProps {
  result: Result
  onViewForm: () => void
  studyId?: StudyId
  requestStatus?: DataAccessRequestStatus
  reviewSubStatus?: DataAccessRequestReviewSubStatus
  dataAccessRequestDetail?: DataAccessRequestDetail
  sharing?: DataAccessRequestSharing
}

const DataAccessRequestFormCard = ({
  result,
  onViewForm,
  studyId,
  requestStatus,
  reviewSubStatus,
  dataAccessRequestDetail,
  sharing
}: DataAccessRequestFormCardProps): JSX.Element => {
  const {
    isReady,
    formSubmission,
    dataAccessRequest,
    requestedAt,
    requestingUserEmail,
    requesterOrganisationName,
    sourceWorkspaceName,
    requesterWorkspaceName,
    datasetColor
  } = getDataAccessRequestFormCardData(result)

  if (!isReady || !formSubmission || !dataAccessRequest || !requestedAt) {
    return (
      <styles.ItemRow $hasBorder={false} data-testid={TEST_ID_DATA_ACCESS_REQUEST_UNAVAILABLE}>
        <styles.ItemSubtitle>{DATA_ACCESS_REQUEST_DETAILS_UNAVAILABLE_MESSAGE}</styles.ItemSubtitle>
      </styles.ItemRow>
    )
  }

  return (
    <>
      <styles.ItemRow>
        <styles.ItemIcon>
          <icons.DocsSmall />
        </styles.ItemIcon>
        <styles.ItemInfo>
          <styles.ItemTitle>{DATA_ACCESS_REQUEST_FORM_TITLE}</styles.ItemTitle>
          <styles.ItemSubtitle>{formSubmission.formName}</styles.ItemSubtitle>
        </styles.ItemInfo>
        <styles.RowActionButton type="tertiary" data-testid="custom-form-view-action" onClick={onViewForm}>
          <icons.EyeSmall />
          <span>View</span>
        </styles.RowActionButton>
      </styles.ItemRow>

      <DataAccessRequestMetadataSection
        requestingUserEmail={requestingUserEmail}
        requestedAt={requestedAt}
        requesterOrganisationName={requesterOrganisationName}
        sourceWorkspaceName={sourceWorkspaceName}
        requesterWorkspaceName={requesterWorkspaceName}
        datasetName={dataAccessRequest.datasetName}
        datasetColor={datasetColor}
        requestStatus={requestStatus ?? mapResultsStateToDataAccessRequestStatus(result.status)}
        reviewSubStatus={reviewSubStatus}
        studyId={studyId}
        querySource={dataAccessRequest}
        dataAccessRequestDetail={dataAccessRequestDetail}
        sharing={sharing}
      />
      <DataAccessRequestGenotypicQuerySection querySource={dataAccessRequest} />
      <DataAccessRequestPhenotypicQuerySection querySource={dataAccessRequest} />
    </>
  )
}

export default DataAccessRequestFormCard
